import { deleteArticle, getAllArticles } from 'http/articleApi';
import { getAllArticleCategories } from 'http/articleCategoryApi';

import {
  IArticle,
  IArticleCategory,
  IArticleParams,
  IAutocompleteTypeParams,
} from 'common/types';
import { notify } from 'common/utils';
import { computed, makeAutoObservable, observable } from 'mobx';
import { ArticleModel } from 'store/article-model';
import { FiltersModel, TParams } from 'store/filters-model';

export class ArticlesViewModel {
  _loading = false;
  _articles: ArticleModel[] = [];
  _count = 0;
  _filters: FiltersModel;
  _categoryOptions: IAutocompleteTypeParams[] = [];
  _selectedCategory: IAutocompleteTypeParams | null;
  _searchCategoryValue: string;

  get loading() {
    return this._loading;
  }

  set loading(value: boolean) {
    this._loading = value;
  }

  set search(value: string) {
    this._filters.search = value;

    this.fetchArticles();
  }

  get articles() {
    return this._articles;
  }

  set articles(articles: ArticleModel[]) {
    this._articles = articles;
  }

  get categoryOptions() {
    return this._searchCategoryValue
      ? this._categoryOptions.filter((option) =>
          option.value
            .toLowerCase()
            .includes(this._searchCategoryValue.toLowerCase()),
        )
      : this._categoryOptions;
  }

  get searchCategoryValue() {
    return this._searchCategoryValue;
  }

  set searchCategoryValue(value: string) {
    this._searchCategoryValue = value;
  }

  get selectedCategory() {
    return this._selectedCategory;
  }

  set selectedCategory(category: IAutocompleteTypeParams | null) {
    this._selectedCategory = category;
    this._filters.page = 1;

    if (category) {
      this._filters.params = {
        ...this._filters.params,
        categoryName: category.value,
        categoryId: String(category.id),
      };

      this.fetchArticles();
    }
  }

  get skeleton() {
    return new Array(5).fill(new ArticleModel());
  }

  get count() {
    return this._count;
  }

  removeArticle = async (id: number) => {
    const updatedArticles = this._articles.filter(
      (article) => article.id !== id,
    );
    const result = await deleteArticle(id);

    if (result) {
      this._articles = updatedArticles;
      notify(
        <div>
          Article <strong>{result.title}</strong> was removed succesfully!
        </div>,
      );
    }
  };

  fetchArticles = async () => {
    const { page, pageSize, search, sort, sortBy } = this._filters;

    const params: IArticleParams = {
      page: page,
      pageSize: pageSize,
      sort: sort,
      sortBy: sortBy,
    };

    if (search) {
      params.title = search;
    }

    if (this.selectedCategory) {
      params.categoryId = String(this.selectedCategory.id);
    }

    this.loading = true;

    try {
      const { count, items, page } = await getAllArticles(params);

      const articleCategories = await getAllArticleCategories();

      this._articles = items.map(
        (article: IArticle) => new ArticleModel(article.id, article),
      );

      this._categoryOptions = articleCategories.map(
        (articleCategory: IArticleCategory) => ({
          id: articleCategory.id,
          value: articleCategory.name,
        }),
      );

      this._count = count;
      this._filters.page = page;

      return items;
    } catch (e) {
      notify(`Error, reason:${e}`, 'error');
    } finally {
      this.loading = false;
    }
  };

  constructor(params?: TParams) {
    this._filters = new FiltersModel(this.fetchArticles, params);
    this._searchCategoryValue = params?.categoryName;
    this._selectedCategory = params?.categoryId
      ? { value: params.categoryName, id: Number(params.categoryId) }
      : null;

    this.fetchArticles();

    makeAutoObservable(this, {
      _loading: observable,
      _articles: observable,
      _selectedCategory: observable,
      _searchCategoryValue: observable,
      _count: observable,

      loading: computed,
    });
  }
}
